/**
 * Resource Board Context - Centralized state management
 */
import React, { createContext, useContext, useReducer, useCallback } from 'react';
import logger from '../utils/logger';

// Action types
export const ACTIONS = {
  // Data loading
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',

  // Data
  SET_PROJECTS: 'SET_PROJECTS',
  SET_USERS: 'SET_USERS',
  SET_ASSIGNMENTS: 'SET_ASSIGNMENTS',
  SET_CARS: 'SET_CARS',
  SET_CAR_ASSIGNMENTS: 'SET_CAR_ASSIGNMENTS',
  SET_TRAVEL_DATA: 'SET_TRAVEL_DATA',
  SET_NOTES: 'SET_NOTES',
  SET_EXPANDED_PROJECTS: 'SET_EXPANDED_PROJECTS',

  // CRUD operations
  ADD_ASSIGNMENT: 'ADD_ASSIGNMENT',
  UPDATE_ASSIGNMENT: 'UPDATE_ASSIGNMENT',
  DELETE_ASSIGNMENT: 'DELETE_ASSIGNMENT',

  ADD_CAR_ASSIGNMENT: 'ADD_CAR_ASSIGNMENT',
  UPDATE_CAR_ASSIGNMENT: 'UPDATE_CAR_ASSIGNMENT',
  DELETE_CAR_ASSIGNMENT: 'DELETE_CAR_ASSIGNMENT',

  ADD_TRAVEL: 'ADD_TRAVEL',
  UPDATE_TRAVEL: 'UPDATE_TRAVEL',
  DELETE_TRAVEL: 'DELETE_TRAVEL',

  ADD_NOTE: 'ADD_NOTE',
  UPDATE_NOTE: 'UPDATE_NOTE',
  DELETE_NOTE: 'DELETE_NOTE',

  // UI state
  TOGGLE_PROJECT_EXPANSION: 'TOGGLE_PROJECT_EXPANSION',
  SET_SELECTED_TIME_RANGE: 'SET_SELECTED_TIME_RANGE',
  SET_CURRENT_DATE: 'SET_CURRENT_DATE',
  SET_FILTERS: 'SET_FILTERS',

  // History (Undo/Redo)
  ADD_TO_UNDO_STACK: 'ADD_TO_UNDO_STACK',
  UNDO: 'UNDO',
  REDO: 'REDO',
  CLEAR_REDO_STACK: 'CLEAR_REDO_STACK'
};

// Initial state
const initialState = {
  // Loading states
  loading: true,
  error: null,

  // Data
  projects: [],
  users: [],
  assignments: [],
  cars: [],
  carAssignments: [],
  travelData: [],
  notes: [],

  // UI state
  expandedProjects: {},
  selectedTimeRange: 'month',
  currentDate: new Date(),

  // Filters
  filters: {
    selectedUsers: [],
    selectedProjects: [],
    selectedCars: [],
    selectedCustomers: [],
    selectedRole: 'all',
    groupByRole: false
  },

  // History
  undoStack: [],
  redoStack: [],
  maxUndoSteps: 10
};

// Reducer function
function resourceBoardReducer(state, action) {
  // Disable logging in production for performance
  // logger.debug('Reducer action:', action.type, action.payload);

  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };

    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    case ACTIONS.SET_PROJECTS:
      return { ...state, projects: action.payload, loading: false };

    case ACTIONS.SET_USERS:
      return { ...state, users: action.payload };

    case ACTIONS.SET_ASSIGNMENTS:
      // Support functional updates
      if (action.functional && typeof action.payload === 'function') {
        return { ...state, assignments: action.payload(state.assignments) };
      }
      return { ...state, assignments: action.payload };

    case ACTIONS.SET_CARS:
      return { ...state, cars: action.payload };

    case ACTIONS.SET_CAR_ASSIGNMENTS:
      return { ...state, carAssignments: action.payload };

    case ACTIONS.SET_TRAVEL_DATA:
      return { ...state, travelData: action.payload };

    case ACTIONS.SET_NOTES:
      return { ...state, notes: action.payload };

    // CRUD operations for assignments
    case ACTIONS.ADD_ASSIGNMENT:
      return {
        ...state,
        assignments: [...state.assignments, action.payload]
      };

    case ACTIONS.UPDATE_ASSIGNMENT:
      return {
        ...state,
        assignments: state.assignments.map(a =>
          a.id === action.payload.id ? { ...a, ...action.payload } : a
        )
      };

    case ACTIONS.DELETE_ASSIGNMENT:
      return {
        ...state,
        assignments: state.assignments.filter(a => a.id !== action.payload)
      };

    // CRUD operations for car assignments
    case ACTIONS.ADD_CAR_ASSIGNMENT:
      return {
        ...state,
        carAssignments: [...state.carAssignments, action.payload]
      };

    case ACTIONS.UPDATE_CAR_ASSIGNMENT:
      return {
        ...state,
        carAssignments: state.carAssignments.map(ca =>
          ca.id === action.payload.id ? { ...ca, ...action.payload } : ca
        )
      };

    case ACTIONS.DELETE_CAR_ASSIGNMENT:
      return {
        ...state,
        carAssignments: state.carAssignments.filter(ca => ca.id !== action.payload)
      };

    // CRUD operations for travel
    case ACTIONS.ADD_TRAVEL:
      return {
        ...state,
        travelData: [...state.travelData, action.payload]
      };

    case ACTIONS.UPDATE_TRAVEL:
      return {
        ...state,
        travelData: state.travelData.map(t =>
          t.id === action.payload.id ? { ...t, ...action.payload } : t
        )
      };

    case ACTIONS.DELETE_TRAVEL:
      return {
        ...state,
        travelData: state.travelData.filter(t => t.id !== action.payload)
      };

    // CRUD operations for notes
    case ACTIONS.ADD_NOTE:
      return {
        ...state,
        notes: [...state.notes, action.payload]
      };

    case ACTIONS.UPDATE_NOTE:
      return {
        ...state,
        notes: state.notes.map(n =>
          n.id === action.payload.id ? { ...n, ...action.payload } : n
        )
      };

    case ACTIONS.DELETE_NOTE:
      return {
        ...state,
        notes: state.notes.filter(n => n.id !== action.payload)
      };

    // UI state
    case ACTIONS.TOGGLE_PROJECT_EXPANSION:
      return {
        ...state,
        expandedProjects: {
          ...state.expandedProjects,
          [action.payload]: !state.expandedProjects[action.payload]
        }
      };

    case ACTIONS.SET_SELECTED_TIME_RANGE:
      return { ...state, selectedTimeRange: action.payload };

    case ACTIONS.SET_CURRENT_DATE:
      return { ...state, currentDate: action.payload };

    case ACTIONS.SET_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload }
      };

    case ACTIONS.SET_EXPANDED_PROJECTS:
      return { ...state, expandedProjects: action.payload };

    // History operations
    case ACTIONS.ADD_TO_UNDO_STACK:
      const newUndoStack = [...state.undoStack, action.payload];
      // Keep only last maxUndoSteps operations
      const trimmedUndoStack = newUndoStack.length > state.maxUndoSteps
        ? newUndoStack.slice(-state.maxUndoSteps)
        : newUndoStack;

      return {
        ...state,
        undoStack: trimmedUndoStack,
        redoStack: [] // Clear redo stack when new operation is added
      };

    case ACTIONS.UNDO:
      if (state.undoStack.length === 0) return state;

      const lastOperation = state.undoStack[state.undoStack.length - 1];
      return {
        ...state,
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, lastOperation]
      };

    case ACTIONS.REDO:
      if (state.redoStack.length === 0) return state;

      const nextOperation = state.redoStack[state.redoStack.length - 1];
      return {
        ...state,
        redoStack: state.redoStack.slice(0, -1),
        undoStack: [...state.undoStack, nextOperation]
      };

    case ACTIONS.CLEAR_REDO_STACK:
      return { ...state, redoStack: [] };

    default:
      logger.warn('Unknown action type:', action.type);
      return state;
  }
}

// Create context
const ResourceBoardContext = createContext();

// Provider component
export function ResourceBoardProvider({ children }) {
  const [state, dispatch] = useReducer(resourceBoardReducer, initialState);

  // Memoized dispatch actions
  const actions = {
    setLoading: useCallback((loading) => {
      dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
    }, []),

    setError: useCallback((error) => {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error });
    }, []),

    setProjects: useCallback((projects) => {
      dispatch({ type: ACTIONS.SET_PROJECTS, payload: projects });
    }, []),

    setUsers: useCallback((users) => {
      dispatch({ type: ACTIONS.SET_USERS, payload: users });
    }, []),

    setAssignments: useCallback((assignmentsOrUpdater) => {
      // Support both direct value and functional updates (like setState)
      if (typeof assignmentsOrUpdater === 'function') {
        // Use dispatch with a special action type for functional updates
        dispatch({ type: ACTIONS.SET_ASSIGNMENTS, payload: assignmentsOrUpdater, functional: true });
      } else {
        // Direct value
        dispatch({ type: ACTIONS.SET_ASSIGNMENTS, payload: assignmentsOrUpdater });
      }
    }, []),

    addAssignment: useCallback((assignment) => {
      dispatch({ type: ACTIONS.ADD_ASSIGNMENT, payload: assignment });
    }, []),

    updateAssignment: useCallback((assignment) => {
      dispatch({ type: ACTIONS.UPDATE_ASSIGNMENT, payload: assignment });
    }, []),

    deleteAssignment: useCallback((id) => {
      dispatch({ type: ACTIONS.DELETE_ASSIGNMENT, payload: id });
    }, []),

    toggleProjectExpansion: useCallback((projectId) => {
      dispatch({ type: ACTIONS.TOGGLE_PROJECT_EXPANSION, payload: projectId });
    }, []),

    setFilters: useCallback((filters) => {
      dispatch({ type: ACTIONS.SET_FILTERS, payload: filters });
    }, []),

    addToUndoStack: useCallback((operation) => {
      dispatch({ type: ACTIONS.ADD_TO_UNDO_STACK, payload: operation });
    }, []),

    undo: useCallback(() => {
      dispatch({ type: ACTIONS.UNDO });
    }, []),

    redo: useCallback(() => {
      dispatch({ type: ACTIONS.REDO });
    }, [])
  };

  const value = { state, dispatch, actions };

  return (
    <ResourceBoardContext.Provider value={value}>
      {children}
    </ResourceBoardContext.Provider>
  );
}

// Custom hook to use the context
export function useResourceBoard() {
  const context = useContext(ResourceBoardContext);

  if (!context) {
    throw new Error('useResourceBoard must be used within ResourceBoardProvider');
  }

  return context;
}

export default ResourceBoardContext;
